/**
 * SSE Service for Real-time Notifications
 *
 * Manages Server-Sent Events (SSE) connection to backend for real-time notification delivery.
 * Implements automatic reconnection with exponential backoff.
 */

interface SSEMessage {
  type: 'connected' | 'notification' | 'error'
  data?: unknown
  timestamp?: string
  userId?: string
}

export class SSEService {
  private eventSource: EventSource | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000 // Start with 1 second
  private onMessageCallback: ((message: SSEMessage) => void) | null = null
  private onErrorCallback: ((error: Event) => void) | null = null
  private url: string

  constructor(baseUrl: string) {
    // baseUrl already includes /api/v1 from VITE_API_URL
    // If baseUrl is relative (starts with /), convert to absolute URL for EventSource
    const absoluteBaseUrl = baseUrl.startsWith('/')
      ? `${window.location.protocol}//${window.location.host}${baseUrl}`
      : baseUrl

    this.url = `${absoluteBaseUrl}/notifications/stream`
  }

  /**
   * Connect to SSE endpoint with authentication token
   */
  connect(token: string, onMessage: (message: SSEMessage) => void): void {
    // Close existing connection if any
    this.disconnect()

    this.onMessageCallback = onMessage

    // EventSource doesn't support custom headers in browser
    // Token must be passed via query parameter or cookie
    const urlWithAuth = `${this.url}?token=${encodeURIComponent(token)}`

    try {
      this.eventSource = new EventSource(urlWithAuth)

      this.eventSource.onopen = () => {
        console.log('[SSE] Connection opened')
        this.reconnectAttempts = 0 // Reset on successful connection
      }

      this.eventSource.onmessage = (event: MessageEvent) => {
        try {
          const message: SSEMessage = JSON.parse(event.data)
          console.log('[SSE] Message received:', message.type)

          if (this.onMessageCallback) {
            this.onMessageCallback(message)
          }
        } catch (error) {
          console.error('[SSE] Failed to parse message:', error)
        }
      }

      this.eventSource.onerror = (event: Event) => {
        console.error('[SSE] Connection error:', event)

        if (this.onErrorCallback) {
          this.onErrorCallback(event)
        }

        // EventSource automatically attempts to reconnect on error
        // But we'll implement our own logic with backoff
        this.handleConnectionError(token, onMessage)
      }
    } catch (error) {
      console.error('[SSE] Failed to create EventSource:', error)
      this.handleConnectionError(token, onMessage)
    }
  }

  /**
   * Handle connection errors with exponential backoff
   */
  private handleConnectionError(token: string, onMessage: (message: SSEMessage) => void): void {
    // Close the errored connection
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // Exponential backoff

    console.log(
      `[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    )

    setTimeout(() => {
      console.log('[SSE] Attempting to reconnect...')
      this.connect(token, onMessage)
    }, delay)
  }

  /**
   * Set error callback
   */
  onError(callback: (error: Event) => void): void {
    this.onErrorCallback = callback
  }

  /**
   * Disconnect from SSE endpoint
   */
  disconnect(): void {
    if (this.eventSource) {
      console.log('[SSE] Disconnecting...')
      this.eventSource.close()
      this.eventSource = null
    }

    this.reconnectAttempts = 0
    this.onMessageCallback = null
    this.onErrorCallback = null
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN
  }

  /**
   * Get current connection state
   */
  getReadyState(): number {
    if (!this.eventSource) {
      return EventSource.CLOSED
    }
    return this.eventSource.readyState
  }
}

/**
 * Singleton instance
 */
let instance: SSEService | null = null

export function getSSEService(baseUrl?: string): SSEService {
  if (!instance) {
    if (!baseUrl) {
      throw new Error('SSEService must be initialized with baseUrl on first call')
    }
    instance = new SSEService(baseUrl)
  }
  return instance
}
